import { Component, type ErrorInfo, type ReactNode } from 'react';

import errorLogger, { BUGSNAG_STATUSES } from '@picsart/growth-rc/services/errorLogger';

import { ErrorFallback } from './ErrorFallback';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

/**
 * Root-level error boundary. Catches uncaught render/lifecycle errors anywhere
 * in the AIWardrobe tree, reports them to Bugsnag, and shows a minimal
 * fallback so the user has a recovery affordance instead of a blank screen.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false };

  // Dedup identical errors so a Try-again loop on a persistent bug doesn't
  // fire one Bugsnag event per retry.
  private reportedKeys = new Set<string>();

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    const key = `${error.message}::${error.stack?.split('\n')[1] ?? ''}`;
    if (this.reportedKeys.has(key)) return;
    this.reportedKeys.add(key);

    console.error('[ErrorBoundary] Uncaught render error:', error, info);
    try {
      errorLogger.trigger(error, BUGSNAG_STATUSES.ERROR);
    } catch {
      // errorLogger may not be initialized in dev — ignore.
    }
  }

  handleReset = (): void => {
    this.setState({ hasError: false });
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    if (this.props.fallback) return this.props.fallback;
    return <ErrorFallback onReset={this.handleReset} />;
  }
}
